import React from 'react';
import Button from '../../../components/ui/Button';

import { formatPrice, formatDate } from '../../../utils';
import invoiceService from '../../../services/invoiceService';

const InvoiceDetails = ({ invoice, onUpdate, onClose }) => {
  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      const result = await invoiceService.updateInvoice(invoice?.id, { status: newStatus });
      if (result?.success) {
        onUpdate?.(result.data);
      }
    } catch (error) {
      console.log('Error updating status:', error);
    }
  };

  // Handle payment marking
  const handleMarkAsPaid = async () => {
    try {
      const result = await invoiceService.updateInvoice(invoice?.id, { 
        status: 'paid',
        payment_date: new Date().toISOString().split('T')[0]
      });
      if (result?.success) {
        onUpdate?.(result.data);
      }
    } catch (error) {
      console.log('Error marking as paid:', error);
    }
  };

  // Calculate if overdue
  const isOverdue = invoice?.status === 'sent' && new Date(invoice?.due_date) < new Date();

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'sent': return 'text-blue-600 bg-blue-100';
      case 'paid': return 'text-green-600 bg-green-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Print invoice
  const handlePrint = () => {
    window.print();
  };

  if (!invoice) return null;

  return (
    <div className="space-y-6">
      {/* Invoice Header */}
      <div className="bg-white">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">INVOICE</h1>
            <p className="text-text-secondary">#{invoice?.invoice_number}</p>
          </div>
          <div className="text-right">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isOverdue ? 'text-red-600 bg-red-100' : getStatusColor(invoice?.status)
            }`}>
              {isOverdue ? 'Overdue' : invoice?.status?.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Business Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          <div>
            <h3 className="font-semibold text-text-primary mb-2">From:</h3>
            <div className="text-text-secondary">
              <p className="font-medium text-text-primary">
                {invoice?.vendor_profiles?.business_name}
              </p>
              {invoice?.vendor_profiles?.business_address && (
                <p>{invoice.vendor_profiles.business_address}</p>
              )}
              {invoice?.vendor_profiles?.phone && (
                <p>Phone: {invoice.vendor_profiles.phone}</p>
              )}
              {invoice?.vendor_profiles?.user_profiles?.email && (
                <p>Email: {invoice.vendor_profiles.user_profiles.email}</p>
              )}
              {invoice?.vendor_profiles?.website && (
                <p>Website: {invoice.vendor_profiles.website}</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-text-primary mb-2">Bill To:</h3>
            <div className="text-text-secondary">
              <p className="font-medium text-text-primary">{invoice?.client_name}</p>
              {invoice?.client_address && (
                <p className="whitespace-pre-line">{invoice.client_address}</p>
              )}
              {invoice?.client_email && <p>Email: {invoice.client_email}</p>}
              {invoice?.client_phone && <p>Phone: {invoice.client_phone}</p>}
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div>
            <p className="text-text-muted text-sm">Invoice Date</p>
            <p className="font-medium text-text-primary">{formatDate(invoice?.invoice_date)}</p>
          </div>
          <div>
            <p className="text-text-muted text-sm">Due Date</p>
            <p className={`font-medium ${
              isOverdue ? 'text-red-600' : 'text-text-primary'
            }`}>
              {formatDate(invoice?.due_date)}
            </p>
          </div>
          {invoice?.payment_date && (
            <div>
              <p className="text-text-muted text-sm">Payment Date</p>
              <p className="font-medium text-green-600">{formatDate(invoice.payment_date)}</p>
            </div>
          )}
        </div>

        {/* Invoice Items */}
        <div className="mb-8">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-border-light">
                  <th className="text-left py-3 text-text-primary font-medium">Description</th>
                  <th className="text-center py-3 text-text-primary font-medium w-20">Qty</th>
                  <th className="text-right py-3 text-text-primary font-medium w-24">Rate</th>
                  <th className="text-right py-3 text-text-primary font-medium w-24">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice?.invoice_items?.map((item) => (
                  <tr key={item?.id} className="border-b border-border-light">
                    <td className="py-3 text-text-secondary">{item?.description}</td>
                    <td className="py-3 text-center text-text-secondary">{item?.quantity}</td>
                    <td className="py-3 text-right text-text-secondary">
                      {formatPrice(item?.unit_price)}
                    </td>
                    <td className="py-3 text-right font-medium text-text-primary">
                      {formatPrice(item?.total_price)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoice Totals */}
        <div className="flex justify-end mb-8">
          <div className="w-full max-w-sm">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-text-secondary">Subtotal:</span>
                <span className="font-medium">{formatPrice(invoice?.subtotal)}</span>
              </div>
              {parseFloat(invoice?.discount_amount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">Discount:</span>
                  <span className="font-medium text-red-600">
                    -{formatPrice(invoice.discount_amount)}
                  </span>
                </div>
              )}
              {parseFloat(invoice?.tax_amount || 0) > 0 && (
                <div className="flex justify-between">
                  <span className="text-text-secondary">
                    Tax ({invoice?.tax_rate}%):
                  </span>
                  <span className="font-medium">{formatPrice(invoice.tax_amount)}</span>
                </div>
              )}
              <div className="border-t border-border-light pt-2">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total:</span>
                  <span className="text-primary">{formatPrice(invoice?.total_amount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Notes and Terms */}
        {(invoice?.notes || invoice?.terms_and_conditions) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {invoice?.notes && (
              <div>
                <h4 className="font-medium text-text-primary mb-2">Notes:</h4>
                <p className="text-text-secondary text-sm whitespace-pre-line">
                  {invoice.notes}
                </p>
              </div>
            )}
            {invoice?.terms_and_conditions && (
              <div>
                <h4 className="font-medium text-text-primary mb-2">Terms & Conditions:</h4>
                <p className="text-text-secondary text-sm whitespace-pre-line">
                  {invoice.terms_and_conditions}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-6 border-t border-border-light">
          <Button variant="primary" iconName="Printer" onClick={handlePrint}>
            Print Invoice
          </Button>
          
          {invoice?.status === 'draft' && (
            <Button 
              variant="outline" 
              onClick={() => handleStatusUpdate('sent')}
            >
              Send Invoice
            </Button>
          )}
          
          {invoice?.status === 'sent' && (
            <Button 
              variant="outline" 
              onClick={handleMarkAsPaid}
            >
              Mark as Paid
            </Button>
          )}
          
          {invoice?.status === 'sent' && (
            <Button 
              variant="outline" 
              onClick={() => handleStatusUpdate('cancelled')}
              className="text-red-600 hover:text-red-700"
            >
              Cancel Invoice
            </Button>
          )}

          <div className="ml-auto">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style jsx>{`
        @media print {
          .fixed, .sticky {
            position: static !important;
          }
          button {
            display: none !important;
          }
          .border-t:last-child {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default InvoiceDetails;